import { useState, useRef, useCallback } from "react";
import { geocodeAddressFree } from "../../services/nominatimGeocoding";
import { extractLocation } from "../../services/addressParser";

/**
 * Address search bar using the same geocoding algorithm as NI/Migration.
 * User types an address and presses Enter → geocodes → calls onPlaceSelected.
 */
export default function AddressSearchBar({ onPlaceSelected, onClear, placeholder }) {
  const inputRef = useRef(null);
  const [value, setValue] = useState("");
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = useCallback(async () => {
    const text = value.trim();
    if (!text) return;

    setLocating(true);
    setError("");

    try {
      // First, try to recognize the municipality/barangay anywhere in the typed
      // text (no commas required) by matching against the known service-area
      // boundaries. This restores the City > Barangay > Street > Number anchor.
      let address = text;
      let barangay = "";
      let municipality = "";

      const extracted = await extractLocation(text);
      if (extracted.matched) {
        address = extracted.address;
        barangay = extracted.barangay;
        municipality = extracted.municipality;
      } else {
        // Fallback: explicit "address, barangay, city" comma format.
        const parts = text.split(",").map((s) => s.trim());
        if (parts.length >= 3) {
          address = parts.slice(0, -2).join(", ");
          barangay = parts[parts.length - 2];
          municipality = parts[parts.length - 1];
        } else if (parts.length === 2) {
          address = parts[0];
          municipality = parts[1];
        }
      }

      const result = await geocodeAddressFree({
        address,
        barangay,
        municipality,
      });

      if (result.success && result.latitude && result.longitude) {
        onPlaceSelected({ lat: result.latitude, lng: result.longitude, address: text });
        setError("");
      } else {
        setError("Location not found");
      }
    } catch {
      setError("Geocoding failed");
    } finally {
      setLocating(false);
    }
  }, [value, onPlaceSelected]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  }, [handleSearch]);

  const handleClear = useCallback(() => {
    setValue("");
    setError("");
    if (onClear) onClear();
  }, [onClear]);

  return (
    <div className="relative flex items-center">
      <div className="absolute left-2.5 text-gray-400 pointer-events-none">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </div>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => { setValue(e.target.value); setError(""); }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || "Search address (press Enter)..."}
        className={`w-full sm:w-80 pl-8 pr-8 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          error ? "border-red-400" : "border-gray-300"
        }`}
      />
      {locating && (
        <div className="absolute right-2 text-blue-500">
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      )}
      {!locating && value && (
        <button
          onClick={handleClear}
          className="absolute right-2 text-gray-400 hover:text-gray-600"
          title="Clear"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
      {error && (
        <span className="absolute -bottom-5 left-0 text-xs text-red-500">{error}</span>
      )}
    </div>
  );
}
